import React, { useState, useEffect } from 'react';
import NavigationBar from '../../components/ui/NavigationBar';
import LoadingState from '../../components/ui/LoadingState';
import TodaysDrill from './components/TodaysDrill';
import WelcomeBanner from './components/WelcomeBanner';
import RealWorldResults from './components/RealWorldResults';
import SkillsProgress from './components/SkillsProgress';
import RecentDrillHistory from './components/RecentDrillHistory';
import ProgressAnalytics from './components/ProgressAnalytics';
import CoachingStageIndicator, { CoachingStageProgress } from '../../components/CoachingStageIndicator';
import LeaderboardWidget from '../../components/LeaderboardWidget';
import UpgradeBanner from '../../components/UpgradeBanner';
import { AchievementNotificationManager } from '../../components/AchievementNotification';
// Data fetching now handled via React Query hook
import { useDashboardData } from '../../hooks/useDashboardData';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTenant } from '../../contexts/TenantContext';
import { useSubscription } from '../../hooks/useSubscription';
import { getPostAuthRedirect } from '../../services/whopPlanService';
// import { tenantTeamService } from '../../services/tenantTeamService'; // TODO: Create this service
import Icon from '../../components/AppIcon';

const HomeDashboard = () => {
  const { user, profile, loading: authLoading } = useAuth();
  const { tier, ugcTier } = useSubscription();
  const { tenantId, tenantSlug } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasRedirected, setHasRedirected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProgress, setShowProgress] = useState(false);
  const [userRole, setUserRole] = useState(null);

  const [recentDrills] = useState([
    {
      id: 1,
      scenario: "Cold Call Opening - Financial Services",
      completedDate: "2025-12-01",
      duration: "7 min",
      averageScore: 82,
      scores: [
        { metric: "Clarity", value: 85 },
        { metric: "Confidence", value: 88 },
        { metric: "Control", value: 78 },
        { metric: "Close", value: 77 }
      ]
    },
    {
      id: 2,
      scenario: "Overcoming Budget Concerns - Real Estate",
      completedDate: "2025-11-30",
      duration: "9 min",
      averageScore: 75,
      scores: [
        { metric: "Clarity", value: 72 },
        { metric: "Confidence", value: 80 },
        { metric: "Control", value: 73 },
        { metric: "Close", value: 75 }
      ]
    },
    {
      id: 3,
      scenario: "Product Demo Follow-up - Healthcare",
      completedDate: "2025-11-29",
      duration: "6 min",
      averageScore: 88,
      scores: [
        { metric: "Clarity", value: 90 },
        { metric: "Confidence", value: 87 },
        { metric: "Control", value: 86 },
        { metric: "Close", value: 89 }
      ]
    }
  ]);

  // React Query-backed dashboard data (only init if we have a user)
  const dashboard = useDashboardData({
    userId: user?.id || null,
    level: profile?.level || 1,
    profile,
    tenantId,
  });

  useEffect(() => {
    // Avoid repeated redirects causing render loops
    if (hasRedirected) return;

    // Redirect to login if not authenticated, preserving tenant context
    if (!authLoading && !user) {
      const tenantQuery = tenantSlug && tenantSlug !== 'default' ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
      const target = `/authentication${tenantQuery}`;
      const current = `${location.pathname}${location.search || ''}`;
      if (current !== target) {
        navigate(target);
        setHasRedirected(true);
      }
      return;
    }

    // Build tenant query for all redirects
    const tenantQuery = tenantSlug && tenantSlug !== 'default' ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';

    // Check Whop plan to determine product access (source of truth)
    if (!authLoading && user && profile) {
      const checkPlanAndRedirect = async () => {
        try {
          const redirect = await getPostAuthRedirect(user.email, profile);
          const target = `${redirect}${tenantQuery}`;
          const current = `${location.pathname}${location.search || ''}`;
          // Only navigate if the target differs from current
          if (
            (redirect === '/app/ugc-dashboard' || redirect === '/app/ugc-onboarding' || redirect === '/app/dashboard' || redirect === '/app/industry-selection') &&
            current !== target
          ) {
            navigate(target);
            setHasRedirected(true);
            return;
          }
        } catch (err) {
          console.warn('Error checking plan redirect:', err);
        }
      };

      checkPlanAndRedirect();

      // Redirect to onboarding if not completed, preserving tenant
      if (!profile.onboarding_completed || !profile.industry) {
        const target = `/app/industry-selection${tenantQuery}`;
        const current = `${location.pathname}${location.search || ''}`;
        if (current !== target) {
          navigate(target);
          setHasRedirected(true);
        }
        return;
      }
    }

    document.title = "Home Dashboard - EME";
  }, [user, authLoading, profile, tenantSlug, location.pathname, location.search, navigate, hasRedirected]);

  // Mirror loading flag only to preserve existing loading UI
  useEffect(() => {
    setIsLoading(dashboard.isLoading);
  }, [dashboard.isLoading]);

  // Check user role in tenant (for team tracker access)
  useEffect(() => {
    if (!tenantId || !user) return;

    async function checkRole() {
      try {
        // const role = await tenantTeamService.getUserRoleInTenant(tenantId);
        // setUserRole(role);
        // TODO: Implement tenantTeamService.getUserRoleInTenant
        setUserRole('member'); // Default to member for now
      } catch (err) {
        console.error('Error checking user role:', err);
      }
    }

    checkRole();
  }, [tenantId, tenantSlug, user]);

  // Login toast reminder using AchievementNotificationManager
  useEffect(() => {
    if (profile?.inactivityWarning && typeof window?.showAchievement === 'function') {
      const msg = `Run a quick drill to keep ${profile.inactivityWarning.xpAtRisk} XP!`;
      window.showAchievement({
        achievement_type: 'streak_master',
        badge_icon: '⏰',
        achievement_name: msg,
        metric_value: `Decay in ${profile.inactivityWarning.daysUntilDecay} day(s)`
      });
    }
  }, [profile?.inactivityWarning]);

  // Show loading only if auth is loading OR critical data is loading AND we have no cached profile
  if (authLoading) {
    return <LoadingState message="Loading your dashboard..." />;
  }

  // User not authenticated - useEffect will redirect
  if (!user) {
    return null;
  }

  // Critical dashboard data loading for the first time
  if (isLoading && !dashboard.profile) {
    return <LoadingState message="Loading your dashboard..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="bg-card border border-border rounded-2xl p-8 max-w-md text-center">
          <h2 className="text-xl font-bold text-foreground mb-4">Oops! Something went wrong</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <button 
            onClick={() => window.location?.reload()} 
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background dark:bg-gradient-to-br dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 bg-gradient-to-br from-gray-50 via-purple-50/30 to-blue-50/30">
      <NavigationBar userStreak={dashboard.profile?.streakDays || 0} />
      <AchievementNotificationManager />
      <main className="main-content max-w-7xl mx-auto">
        {/* Upgrade Banner for Free Users */}
        <UpgradeBanner />
        
        {/* Hero Header with Lumo Wisdom & Streak */}
        <WelcomeBanner 
          userProfile={dashboard.profile}
          streakDays={dashboard.profile?.streakDays || 0}
        />

        {/* Show Progress or Home */}
        {showProgress ? (
          <div>
            <button
              onClick={() => setShowProgress(false)}
              className="mb-6 flex items-center gap-2 text-primary font-semibold hover:text-primary/80 transition-colors"
            >
              ← Back to Home
            </button>
            <ProgressAnalytics 
              drillHistory={dashboard.drillHistory}
              objectionAttempts={dashboard.objectionAttempts}
            />
          </div>
        ) : (
          <>
            {/* Main Content Grid - 2x2 Equal Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Today's Drill */}
              <div className="transform transition-all hover:scale-[1.005]">
                {dashboard.todaysDrill ? (
                  <TodaysDrill drill={dashboard.todaysDrill} isCompleted={dashboard.todaysDrill?.isCompleted} />
                ) : (
                  <div className="bg-card/80 backdrop-blur-sm border-2 border-dashed border-border rounded-3xl p-8 text-center shadow-lg dark:bg-slate-800 dark:border-slate-600">
                    <p className="text-muted-foreground text-lg">No drill available for today</p>
                  </div>
                )}
              </div>

              {/* Real World Results */}
              <div className="transform transition-all hover:scale-[1.005]">
                <RealWorldResults />
              </div>

              {/* Skills Progress */}
              <div className="transform transition-all hover:scale-[1.005]">
                <SkillsProgress 
                  skills={dashboard.skillsProgress}
                  onProgressClick={() => setShowProgress(true)}
                />
              </div>

              {/* Recent Drill History */}
              <div className="transform transition-all hover:scale-[1.005]">
                <RecentDrillHistory drills={dashboard.drillHistory} />
              </div>
            </div>

            {/* Leaderboard - Full Width */}
            <div className="mb-8">
              <div className="transform transition-all hover:scale-[1.005]">
                <LeaderboardWidget 
                  userStats={dashboard.userLeaderboardStats}
                  leaderboard={dashboard.leaderboard}
                />
              </div>
            </div>

            {/* Team Tracker - For Account Owners */}
            {userRole === 'owner' && (
              <div className="mb-8 transform transition-all hover:scale-[1.005]">
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-slate-800 dark:to-slate-800 rounded-3xl p-8 shadow-lg border border-emerald-200 dark:border-slate-700">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-emerald-900 dark:text-emerald-400 mb-2 flex items-center gap-2">
                        <Icon name="Users" size={28} className="text-emerald-600 dark:text-emerald-400" />
                        Team Progress Tracker
                      </h2>
                      <p className="text-emerald-700 dark:text-emerald-300">Monitor your team's performance and engagement across all modules</p>
                    </div>
                  </div>
                  <button
                    onClick={() => navigate(`/app/team-tracker${tenantSlug && tenantSlug !== 'default' ? `?tenant=${tenantSlug}` : ''}`)}
                    className="inline-block px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-200 hover:from-emerald-600 hover:to-teal-700"
                  >
                    View Team Analytics
                  </button>
                </div>
              </div>
            )}

            {/* Coaching Stage Evolution Section - Moved to Bottom */}
            {/* Only show for default tenant, not for white-label tenants */}
            {tenantSlug === 'default' && (
            <div className="mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-800 dark:to-slate-800 rounded-3xl p-8 shadow-lg border border-blue-100 dark:border-slate-700">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 dark:from-blue-400 to-indigo-600 dark:to-indigo-400 bg-clip-text text-transparent">
                  Your Coaching Evolution
                </h2>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <CoachingStageIndicator 
                  skillLevel={dashboard.profile?.skillLevel || dashboard.profile?.skill_level || 'beginner'} 
                  variant="card"
                  showDescription={true}
                />
                <CoachingStageProgress 
                  level={dashboard.profile?.level || 1}
                  skillLevel={dashboard.profile?.skillLevel || dashboard.profile?.skill_level || 'beginner'}
                />
              </div>
            </div>
            )}
          </>
        )}
      </main></div>
  );
};

export default HomeDashboard;